// gioco/festa.ts — la festa di fine partita: coriandoli, suono e vibrazione.
// Il suono è sintetizzato al momento (Web Audio): nessun file da scaricare, funziona anche offline.
// Con "riduci animazioni" del telefono: niente coriandoli in volo, solo un bagliore; suono e vibrazione brevi.
import { NATIVA, vibra as vibraNativo } from "../servizi/nativo.ts";
import { leggiPreferenza } from "../servizi/preferenze.ts";
import { PREFERENZE } from "../costanti.ts";

export const riduciAnimazioni = (): boolean => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
export const suoniAttivi = (): boolean => leggiPreferenza(PREFERENZE.suoni) !== "no";

// --- coriandoli ---------------------------------------------------------------------
interface Pezzo {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  largo: number;
  alto: number;
  colore: string;
  forma: 0 | 1 | 2; // rettangolo, cerchio, stellina
}

const PEZZI = 160;
const GRAVITA = 0.28;
const ATTRITO = 0.992;

/** Lancia i coriandoli su un canvas a tutto schermo per `durataMs`; restituisce la funzione per fermarli. */
export function coriandoli(canvas: HTMLCanvasElement, colori: string[], durataMs: number): () => void {
  const ctx = canvas.getContext("2d");
  if (!ctx || riduciAnimazioni()) return () => undefined;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const misura = () => {
    canvas.width = canvas.clientWidth * dpr;
    canvas.height = canvas.clientHeight * dpr;
  };
  misura();
  const w = () => canvas.width;
  const h = () => canvas.height;
  const pezzi: Pezzo[] = [];
  const lancio = (lato: -1 | 1) => {
    for (let i = 0; i < PEZZI / 2; i++) {
      const angolo = (-Math.PI / 2) + lato * (Math.PI / 5) * Math.random() - lato * 0.15;
      const forza = (10 + Math.random() * 12) * dpr;
      pezzi.push({
        x: lato === -1 ? w() * 0.08 : w() * 0.92,
        y: h() * 0.9,
        vx: Math.cos(angolo) * forza * -lato * -1,
        vy: Math.sin(angolo) * forza,
        rot: Math.random() * Math.PI,
        vrot: (Math.random() - 0.5) * 0.35,
        largo: (6 + Math.random() * 6) * dpr,
        alto: (10 + Math.random() * 8) * dpr,
        colore: colori[Math.floor(Math.random() * colori.length)]!,
        forma: (Math.random() < 0.6 ? 0 : Math.random() < 0.6 ? 1 : 2) as 0 | 1 | 2,
      });
    }
  };
  lancio(-1);
  lancio(1);
  const inizio = performance.now();
  let fermo = false;
  let raf = 0;
  const disegna = (ora: number) => {
    if (fermo) return;
    const trascorso = ora - inizio;
    ctx.clearRect(0, 0, w(), h());
    const svanisci = Math.max(0, Math.min(1, (durataMs - trascorso) / 600));
    for (const p of pezzi) {
      p.vy += GRAVITA * dpr;
      p.vx *= ATTRITO;
      p.vy *= ATTRITO;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vrot;
      ctx.save();
      ctx.globalAlpha = svanisci;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.colore;
      if (p.forma === 0) ctx.fillRect(-p.largo / 2, -p.alto / 2, p.largo, p.alto * Math.abs(Math.cos(p.rot * 2)) + 2);
      else if (p.forma === 1) {
        ctx.beginPath();
        ctx.arc(0, 0, p.largo / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          const a = (i * 4 * Math.PI) / 5;
          ctx.lineTo(Math.cos(a) * p.largo * 0.7, Math.sin(a) * p.largo * 0.7);
        }
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }
    if (trascorso < durataMs) raf = requestAnimationFrame(disegna);
    else ctx.clearRect(0, 0, w(), h());
  };
  raf = requestAnimationFrame(disegna);
  window.addEventListener("resize", misura);
  return () => {
    fermo = true;
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", misura);
    ctx.clearRect(0, 0, w(), h());
  };
}

// --- suono ------------------------------------------------------------------------------
let audio: AudioContext | null = null;
function contesto(): AudioContext | null {
  try {
    audio ??= new AudioContext();
    if (audio.state === "suspended") void audio.resume();
    return audio;
  } catch {
    return null;
  }
}

function nota(ac: AudioContext, freq: number, inizio: number, durata: number, tipo: OscillatorType, volume: number): void {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = tipo;
  o.frequency.setValueAtTime(freq, inizio);
  g.gain.setValueAtTime(0.0001, inizio);
  g.gain.exponentialRampToValueAtTime(volume, inizio + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, inizio + durata);
  o.connect(g).connect(ac.destination);
  o.start(inizio);
  o.stop(inizio + durata + 0.05);
}

/**
 * Su iPhone l'audio parte solo se il primo suono nasce da un tocco: si "sblocca" sul tocco di «Sono pronto»,
 * così i suoni successivi (conto, VIA, festa) funzionano anche se arrivano dal server.
 */
export function sbloccaAudio(): void {
  const ac = contesto();
  if (!ac) return;
  const o = ac.createOscillator();
  const g = ac.createGain();
  g.gain.value = 0;
  o.connect(g).connect(ac.destination);
  o.start();
  o.stop(ac.currentTime + 0.01);
}

/** Vittoria: arpeggio in salita. Sconfitta: "wah-wah" in discesa (ironico, non triste). Via: un bip. */
export function suono(tipo: "vittoria" | "sconfitta" | "via" | "conto"): void {
  if (!suoniAttivi()) return;
  const ac = contesto();
  if (!ac) return;
  const t = ac.currentTime + 0.02;
  const breve = riduciAnimazioni();
  if (tipo === "vittoria") {
    const note = breve ? [523.25, 783.99] : [523.25, 659.25, 783.99, 1046.5, 1318.5];
    note.forEach((f, i) => nota(ac, f, t + i * 0.11, 0.35, "triangle", 0.25));
  } else if (tipo === "sconfitta") {
    [392, 369.99, 349.23, 293.66].slice(0, breve ? 2 : 4).forEach((f, i) => nota(ac, f, t + i * 0.28, 0.4, "sawtooth", 0.08));
  } else if (tipo === "via") {
    nota(ac, 880, t, 0.25, "square", 0.12);
  } else {
    nota(ac, 440, t, 0.12, "square", 0.08);
  }
}

/** Vibrazione (dove c'è): lunga per la vittoria, breve per gli altri eventi. */
export function vibra(tipo: "vittoria" | "sconfitta" | "via"): void {
  // v030: nelle app le vibrazioni del sistema (su iPhone navigator.vibrate non esiste)
  if (NATIVA) return vibraNativo(tipo === "vittoria" ? "successo" : tipo === "sconfitta" ? "errore" : "forte");
  try {
    const schema = { vittoria: [60, 40, 60, 40, 180], sconfitta: [200], via: [40] }[tipo];
    navigator.vibrate?.(riduciAnimazioni() ? [schema[0]!] : schema);
  } catch {
    // non disponibile
  }
}
